import { z } from 'zod';
import { idSchema } from './common.js';
/** Auth, session and account payloads (Phase 1). Households live in households.ts. */
export const emailSchema = z
    .email()
    .max(254)
    .transform((s) => s.trim().toLowerCase());
export const passwordSchema = z
    .string()
    .min(10, 'Use at least 10 characters')
    .max(200)
    .refine((s) => !/^\s|\s$/.test(s), 'No leading or trailing spaces');
export const displayNameSchema = z.string().trim().min(1).max(80);
export const tokenSchema = z.string().min(20).max(200);
export const HOUSEHOLD_ROLES = ['owner', 'adult', 'member'];
export const householdRoleSchema = z.enum(HOUSEHOLD_ROLES);
export const unitSystemSchema = z.enum(['us', 'metric']);
export const userDtoSchema = z.object({
    id: idSchema,
    email: z.string(),
    displayName: z.string(),
    isAdmin: z.boolean(),
    emailVerifiedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
export const householdDtoSchema = z.object({
    id: idSchema,
    name: z.string(),
    defaultServings: z.number().int().positive(),
    unitSystem: unitSystemSchema,
    timezone: z.string(),
    /** Hours before serve time a thaw reminder is suggested for (D28). */
    thawLeadHours: z.number().int().positive(),
    disabledAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
export const membershipDtoSchema = z.object({
    household: householdDtoSchema,
    role: householdRoleSchema,
    joinedAt: z.iso.datetime(),
});
/** GET /api/v1/auth/me */
export const meResponseSchema = z.object({
    user: userDtoSchema,
    /** The active household for this session (null until the user creates/joins one). */
    household: householdDtoSchema.nullable(),
    role: householdRoleSchema.nullable(),
    memberships: z.array(membershipDtoSchema),
});
/** GET /api/v1/auth/config — public settings the sign-up screen needs before it renders. */
export const captchaProviderSchema = z.enum(['none', 'turnstile']);
export const authConfigResponseSchema = z.object({
    captcha: z.object({
        provider: captchaProviderSchema,
        /** Public widget key; null when `provider` is `none`. */
        siteKey: z.string().nullable(),
    }),
    /** Always true: accounts must confirm their email before using the app (D25). */
    emailVerificationRequired: z.literal(true),
    /** Optional server features the clients show or hide. Missing on older servers = all off. */
    features: z
        .object({
        /** POST /imports/photo is available (the server has an AI provider key). */
        photoImport: z.boolean(),
    })
        .default({ photoImport: false }),
});
/** POST /api/v1/auth/register — creates the user; with `householdName` also creates
 *  a household (owner), with `inviteCode` joins one instead. Sends the verification email.
 *  `captchaToken` is required whenever `GET /auth/config` reports a captcha provider. */
export const registerRequestSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
    displayName: displayNameSchema,
    householdName: z.string().trim().min(1).max(120).optional(),
    inviteCode: z.string().trim().min(4).max(64).optional(),
    captchaToken: z.string().min(1).max(4096).optional(),
});
/** POST /api/v1/auth/login */
export const loginRequestSchema = z.object({
    email: emailSchema,
    password: z.string().min(1).max(200),
    deviceName: z.string().trim().max(120).optional(),
});
/** POST /api/v1/auth/verify-email */
export const verifyEmailRequestSchema = z.object({ token: tokenSchema });
/** POST /api/v1/auth/resend-verification (authenticated) */
/** POST /api/v1/auth/forgot-password — always 200, never reveals whether the email exists */
export const forgotPasswordRequestSchema = z.object({ email: emailSchema });
/** POST /api/v1/auth/reset-password */
export const resetPasswordRequestSchema = z.object({
    token: tokenSchema,
    password: passwordSchema,
});
/** POST /api/v1/auth/change-password (authenticated) */
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1).max(200),
    newPassword: passwordSchema,
});
/** PATCH /api/v1/auth/me */
export const updateProfileRequestSchema = z.object({
    displayName: displayNameSchema.optional(),
});
/** DELETE /api/v1/auth/me — account deletion (Apple 5.1.1(v) / Play policy) */
export const deleteAccountRequestSchema = z.object({
    password: z.string().min(1).max(200),
    /** Required when the user is the sole owner of a household with other members. */
    transferOwnershipTo: idSchema.optional(),
});
/** Sessions ("Devices" page): GET /api/v1/auth/sessions, DELETE /api/v1/auth/sessions/:id */
export const sessionDtoSchema = z.object({
    id: idSchema,
    kind: z.enum(['web', 'refresh']),
    deviceName: z.string().nullable(),
    createdAt: z.iso.datetime(),
    lastSeenAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    current: z.boolean(),
});
export const sessionsResponseSchema = z.object({ sessions: z.array(sessionDtoSchema) });
/** Passkeys (WebAuthn, D16). Option/response shapes are the @simplewebauthn JSON types;
 *  they are passed through as `unknown` here and validated by the library server-side. */
export const passkeyDtoSchema = z.object({
    id: idSchema,
    deviceName: z.string().nullable(),
    createdAt: z.iso.datetime(),
    lastUsedAt: z.iso.datetime().nullable(),
    transports: z.array(z.string()),
});
export const passkeysResponseSchema = z.object({ passkeys: z.array(passkeyDtoSchema) });
export const passkeyRegisterVerifyRequestSchema = z.object({
    response: z.unknown(),
    deviceName: z.string().trim().max(120).optional(),
});
export const passkeyLoginOptionsRequestSchema = z.object({ email: emailSchema.optional() });
export const passkeyLoginVerifyRequestSchema = z.object({
    response: z.unknown(),
    deviceName: z.string().trim().max(120).optional(),
});
export const renamePasskeyRequestSchema = z.object({
    deviceName: z.string().trim().min(1).max(120),
});
/**
 * Mobile device tokens (Phase 5). Opaque bearer tokens issued per device —
 * `POST /auth/token` (login), `POST /auth/token/refresh` (rotate),
 * `POST /auth/token/revoke` (logout). Bearer auth is accepted anywhere a
 * session cookie is; the "me" fields below match `meResponseSchema` exactly.
 */
export const deviceTokenRequestSchema = z.object({
    email: emailSchema,
    password: z.string().min(1).max(200),
    deviceName: z.string().trim().min(1).max(80),
});
/** POST /api/v1/auth/token/refresh and .../revoke */
export const refreshTokenRequestSchema = z.object({ refreshToken: tokenSchema });
export const deviceTokenResponseSchema = z.object({
    accessToken: z.string(),
    accessTokenExpiresAt: z.iso.datetime(),
    refreshToken: z.string(),
    user: userDtoSchema,
    household: householdDtoSchema.nullable(),
    role: householdRoleSchema.nullable(),
    memberships: z.array(membershipDtoSchema),
});
/** GET /api/v1/auth/devices, DELETE /api/v1/auth/devices/:id ("Devices" page, mobile). */
export const deviceDtoSchema = z.object({
    id: idSchema,
    deviceName: z.string(),
    createdAt: z.iso.datetime(),
    lastUsedAt: z.iso.datetime(),
    /** True for the device session making the request; always false from a cookie session. */
    current: z.boolean(),
});
export const devicesResponseSchema = z.object({ devices: z.array(deviceDtoSchema) });
export const okResponseSchema = z.object({ ok: z.literal(true) });
/** Every error body the API returns (Fastify's shape plus an optional machine code). */
export const apiErrorSchema = z.object({
    statusCode: z.number().int(),
    error: z.string(),
    message: z.string(),
    code: z.string().optional(),
});
//# sourceMappingURL=auth.js.map